import React from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  useChangeUserOrganizationMutation,
  useGetOrganizationsQuickQuery,
  useGetUsersQuickQuery,
} from 'src/common/apis/AdminApi';
import { useAppSelector } from 'src/common/hooks/reduxHooks';
import { useUserRole } from 'src/common/hooks/useUserRole';
import { UserRoles, User } from 'src/common/types/User';
import SnackbarUtils from 'src/common/utils/SnackbarUtilsConfigurator';

import { ReactComponent as CancelIconBlue } from '../../../../common/assets/icons/cancel_icon_blue.svg';
import { ReactComponent as SearchIcon } from '../../../../common/assets/icons/search_icon_small_gray.svg';
import OrganizationInput from '../../OrganizationCreateScreen/components/OrganizationInput';

type Props = {
  onClose: () => void;
  userToAdd?: User;
};

const AddUserToOrganizationPopup: React.FC<Props> = ({
  onClose,
  userToAdd,
}) => {
  const { userRole } = useUserRole();

  const { user } = useAppSelector((state) => state.model);

  const [selectedUser, setSelectedUser] = React.useState<string>(
    userToAdd?.email ?? ''
  );
  const [selectedOrganization, setSelectedOrganization] =
    React.useState<string>(user?.organization?.name ?? '');

  const { data: organizations, isFetching: orgsIsLoading } =
    useGetOrganizationsQuickQuery(undefined, {
      skip: userRole !== UserRoles.SUPER_ADMIN,
      refetchOnMountOrArgChange: true,
    });

  const { data: users, isFetching: usersIsLoading } = useGetUsersQuickQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [changeOrganization, { isLoading: isUpdating }] =
    useChangeUserOrganizationMutation();

  const submit = async () => {
    if (!selectedOrganization) return;

    const user = users?.find((item) => item.email === selectedUser);
    if (!user?.id) {
      SnackbarUtils.error(
        'There has been an error with the user. Please select it again.'
      );
      return;
    }

    changeOrganization({
      userId: user.id,
      organizationName: selectedOrganization,
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        onClose();
      });
  };

  return (
    <Box
      sx={{
        maxWidth: '456px',
        minHeight: '240px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '40px',
        flexDirection: 'column',
      }}
    >
      <Box className='row' sx={{ alignItems: 'center' }}>
        <IconButton
          onClick={onClose}
          sx={{ alignSelf: 'flex-start', justifySelf: 'right' }}
        >
          <CancelIconBlue />
        </IconButton>
        <Typography
          sx={{
            alignSelf: 'center',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            mb: '24px',
            ml: 'auto',
            mr: 'auto',
          }}
          color='text.primary'
          variant='h5'
        >
          Add User To Organization
        </Typography>
        <Box sx={{ mr: 'auto' }} />
      </Box>
      <Box>
        <Autocomplete
          disabled={userRole !== UserRoles.SUPER_ADMIN}
          id='organization-autocomplete-input-popup'
          options={organizations ? organizations.map((item) => item.name) : []}
          onChange={(_event, newValue) =>
            setSelectedOrganization(newValue ?? '')
          }
          loading={orgsIsLoading || usersIsLoading || isUpdating}
          getOptionLabel={(option) => option}
          defaultValue={user?.organization?.name ?? undefined}
          renderInput={(params) => (
            <OrganizationInput
              {...params}
              title='Organization'
              startIcon={
                userRole !== UserRoles.SUPER_ADMIN ? <></> : <SearchIcon />
              }
              disabled={
                orgsIsLoading ||
                usersIsLoading ||
                isUpdating ||
                userRole !== UserRoles.SUPER_ADMIN
              }
            />
          )}
        />
      </Box>
      <Box style={{ marginTop: '24px' }}>
        <Autocomplete
          id='users-autocomplete-input-popup'
          options={users ? users.map((item) => item.email) : []}
          disabled={userToAdd !== undefined}
          defaultValue={userToAdd?.email ?? undefined}
          onChange={(_event, newValue) => setSelectedUser(newValue ?? '')}
          loading={orgsIsLoading || usersIsLoading || isUpdating}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <OrganizationInput
              {...params}
              title='User'
              startIcon={userToAdd !== undefined ? <></> : <SearchIcon />}
              disabled={
                orgsIsLoading ||
                usersIsLoading ||
                isUpdating ||
                userToAdd !== undefined
              }
            />
          )}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          display: 'flex',
          marginTop: '20px',
        }}
      >
        <Button
          onClick={onClose}
          disableElevation
          color='appGray2'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
            marginRight: '15px',
          }}
        >
          <Typography color='white'>Cancel</Typography>
        </Button>
        <LoadingButton
          onClick={submit}
          disabled={
            orgsIsLoading ||
            usersIsLoading ||
            isUpdating ||
            !selectedUser ||
            !selectedOrganization
          }
          loading={orgsIsLoading || usersIsLoading || isUpdating}
          disableElevation
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
          }}
        >
          <Typography color='white'>Ok</Typography>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default AddUserToOrganizationPopup;
